import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Sort } from '@angular/material/sort';
import { Subscription, take } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { AddEditAddressUpgradePage } from 'src/pages/properties/add-edit-address-upgrade/add-edit-address-upgrade';
import { AddressUpgradesService } from 'src/shared/services/address-upgrades.service';
import { AddressUpgrade, UPGRADE_STATUS_OPTIONS, UpgradeStatus } from 'src/models/address-upgrade.model';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { UpgradeCardComponent } from 'src/pages/properties/components/upgrade-card/upgrade-card.component';
import {
  getUpgradeCostLabel,
  getUpgradeIconSrc,
  getUpgradeImpactClass,
  getUpgradeImpactLabel,
  getUpgradeRequestedDaysAgo,
  getUpgradeRoiLabel,
  getUpgradeRoiPercent,
} from 'src/pages/properties/components/shared/upgrade-visuals.util';

type SortKey = 'roi_desc' | 'cost_asc' | 'impact_desc' | 'recent' | 'name_asc';

interface UpgradeTableRow {
  upgrade: AddressUpgrade;
  [column: string]: any;
}

const SORT_OPTIONS: { value: SortKey; viewValue: string }[] = [
  { value: 'roi_desc', viewValue: 'ROI (high to low)' },
  { value: 'cost_asc', viewValue: 'Cost (low to high)' },
  { value: 'impact_desc', viewValue: 'Impact (high to low)' },
  { value: 'name_asc', viewValue: 'Name (A to Z)' },
  { value: 'recent', viewValue: 'Recently added' },
];

// Clicking a sortable column header picks the matching sort option, so the header
// and the sort dropdown always agree (Description has no sort of its own).
const COLUMN_SORT_KEYS: Record<string, SortKey> = {
  'Name': 'name_asc',
  'Cost': 'cost_asc',
  'Impact': 'impact_desc',
  'ROI': 'roi_desc',
};

function escapeHtml(value: string): string {
  return (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

@Component({
  selector: 'upgrades-content',
  templateUrl: './upgrades-content.component.html',
  styleUrls: ['./upgrades-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TidyUiComponentsModule, SharedModule, UpgradeCardComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UpgradesContentComponent implements OnInit, OnDestroy {
  readonly windowService = inject(WindowService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly client = inject(Client);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly upgradesService = inject(AddressUpgradesService);
  private readonly demoBlock = inject(DemoBlockService);

  readonly sortOptions = SORT_OPTIONS;

  form = this.fb.group({
    selectedAddress: [0],
    sortKey: ['roi_desc' as SortKey],
    search: [''],
  });

  /**
   * First-load gate. While `false`, the page shows the skeleton (which
   * carries the float-in entrance animation). Flips to `true` once the
   * first upgrades fetch settles and never flips back — filter refetches
   * swap rows in place without replaying the skeleton.
   */
  isDataLoaded = false;
  errorMessage = '';
  addressFilter: { value: number; viewValue: string }[] = [];
  upgrades: AddressUpgrade[] = [];
  rawUpgrades: AddressUpgrade[] = [];
  readonly tableHeaders = ['Name', 'Description', 'Cost', 'Impact', 'ROI', 'Status', 'last-column'];
  tableRows: UpgradeTableRow[] = [];
  private searchTerm = '';
  private panelClosedSubscription$?: Subscription;

  async ngOnInit() {
    this.form.get('sortKey')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setUpgrades(this.rawUpgrades);
        this.cdr.markForCheck();
      });
    this.form.get('search')?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.searchTerm = (value ?? '').trim().toLowerCase();
        this.setUpgrades(this.rawUpgrades);
        this.cdr.markForCheck();
      });
    try {
      await this.buildAddressFilter();
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load upgrades';
      this.util.showError(this.errorMessage, 8000);
      // Drop the skeleton on a failed first load — the error message is the
      // loading outcome, not an endless shimmer.
      this.isDataLoaded = true;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.panelClosedSubscription$?.unsubscribe();
  }

  async loadPage() {
    try {
      const addressId = this.form.value.selectedAddress || undefined;
      this.rawUpgrades = await this.upgradesService.getAddressUpgrades(addressId);
    } catch {
      this.rawUpgrades = [];
    }
    this.setUpgrades(this.rawUpgrades);
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  onFilterValueChange() {
    this.loadPage();
  }

  onTableSortChange(sort: Sort) {
    const key = COLUMN_SORT_KEYS[sort?.active];
    if (!key) {
      return;
    }
    this.form.get('sortKey')?.setValue(key);
  }

  onTableColumnClick(event: { column: string; element: UpgradeTableRow }) {
    // The status cell owns its own clicks (onStatusButtonClick), so it never opens the editor.
    if (event?.column === 'Status') {
      return;
    }
    const upgrade = event?.element?.upgrade;
    if (!upgrade) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.onEditUpgrade(upgrade);
  }

  /**
   * The status buttons live inside a cell's HTML (tidy-table renders cell values
   * via innerHTML), so their clicks are caught by delegation on the table host
   * using the data-upgrade-* attributes rather than by a template binding.
   */
  onStatusButtonClick(event: Event) {
    const button = (event.target as HTMLElement)?.closest?.('[data-upgrade-status]');
    if (!button) {
      return;
    }
    const status = button.getAttribute('data-upgrade-status') as UpgradeStatus;
    const upgrade = this.upgrades.find(u => u.id === Number(button.getAttribute('data-upgrade-id')));
    if (!upgrade || upgrade.status === status) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.onStatusChange({ upgrade, status });
  }

  onStatusChange(event: { upgrade: AddressUpgrade; status: UpgradeStatus }) {
    const { upgrade, status } = event;
    const previousStatus = upgrade.status;
    upgrade.status = status;
    this.tableRows = this.buildTableRows(this.upgrades);
    this.cdr.markForCheck();
    this.upgradesService.updateAddressUpgrade(upgrade.id, { status })
      .catch(err => {
        upgrade.status = previousStatus;
        this.tableRows = this.buildTableRows(this.upgrades);
        this.errorMessage = err?.error?.message || err?.message || 'Failed to update status';
        this.util.showError(this.errorMessage, 5000);
        this.cdr.markForCheck();
      });
  }

  onEditUpgrade(upgrade: AddressUpgrade) {
    this.goToPage('add-edit-address-upgrade', { upgrade }, AddEditAddressUpgradePage);
  }

  goToAddUpgradePage() {
    if (this.demoBlock.block()) { return; }
    const params: Record<string, any> = {};
    const addressId = this.form.value.selectedAddress;
    if (addressId) {
      params.addressId = addressId;
    }
    this.goToPage('add-edit-address-upgrade', params, AddEditAddressUpgradePage);
  }

  private async buildAddressFilter() {
    const addresses = await this.client.getAddresses();
    this.addressFilter = this.client.parseAddressList(addresses, false);
    const firstAddressId = this.addressFilter[0]?.value ?? 0;
    this.form.patchValue({ selectedAddress: firstAddressId }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private setUpgrades(list: AddressUpgrade[]) {
    this.upgrades = this.applyView(list);
    this.tableRows = this.buildTableRows(this.upgrades);
  }

  private buildTableRows(list: AddressUpgrade[]): UpgradeTableRow[] {
    return list.map(upgrade => ({
      upgrade,
      'Name': { value: this.nameCellHtml(upgrade) },
      'Description': { value: this.descriptionCellHtml(upgrade) },
      'Cost': { value: getUpgradeCostLabel(upgrade) },
      'Impact': { value: this.impactCellHtml(upgrade) },
      'ROI': { value: this.roiCellHtml(upgrade) },
      'Status': { value: this.statusCellHtml(upgrade) },
      'last-column': { value: '' },
    }));
  }

  /* tidy-table renders a cell's value as HTML, so the icon tile, impact pill,
     ROI bar and status buttons are emitted as markup here and styled by the
     .upgrades-cell__* rules in this component's stylesheet. They are copies of
     tidy-card-icon-block / tidy-impact-pill / tidy-roi-bar / tidy-small-button
     rather than the components themselves, because those components are only
     instantiated by the mobile card — their styles would not be loaded here. */
  private nameCellHtml(upgrade: AddressUpgrade): string {
    const requestedDaysAgo = upgrade.status === 'requested' ? getUpgradeRequestedDaysAgo(upgrade) : null;
    const requestedPill = requestedDaysAgo
      ? `<span class="upgrades-cell__requested-pill">Requested ${escapeHtml(requestedDaysAgo)}</span>`
      : '';
    return '<span class="upgrades-cell__name">' +
      `<span class="upgrades-cell__icon ${getUpgradeImpactClass(upgrade)}">` +
      `<img src="${getUpgradeIconSrc(upgrade)}" alt="" /></span>` +
      `<span class="upgrades-cell__name-text">${escapeHtml(upgrade.name)}</span>${requestedPill}</span>`;
  }

  private descriptionCellHtml(upgrade: AddressUpgrade): string {
    if (!upgrade.description) {
      return '<span class="upgrades-cell__muted">—</span>';
    }
    return `<span class="upgrades-cell__description">${escapeHtml(upgrade.description)}</span>`;
  }

  private impactCellHtml(upgrade: AddressUpgrade): string {
    const label = getUpgradeImpactLabel(upgrade);
    if (!label) {
      return '<span class="upgrades-cell__muted">—</span>';
    }
    return `<span class="upgrades-cell__impact ${getUpgradeImpactClass(upgrade)}">${label}</span>`;
  }

  private roiCellHtml(upgrade: AddressUpgrade): string {
    const track = upgrade.roi_score === null || upgrade.roi_score === undefined
      ? ''
      : `<span class="upgrades-cell__roi-track"><span class="upgrades-cell__roi-fill" style="width: ${getUpgradeRoiPercent(upgrade)}%"></span></span>`;
    return '<span class="upgrades-cell__roi">' +
      `<span class="upgrades-cell__roi-score">${getUpgradeRoiLabel(upgrade)}</span>${track}</span>`;
  }

  private statusCellHtml(upgrade: AddressUpgrade): string {
    const buttons = UPGRADE_STATUS_OPTIONS.map(option =>
      `<span class="upgrades-cell__status-button${upgrade.status === option.value ? ' is-selected' : ''}"` +
      ` data-upgrade-id="${upgrade.id}" data-upgrade-status="${option.value}">` +
      `<span class="upgrades-cell__status-label">${option.viewValue}</span></span>`).join('');
    return `<span class="upgrades-cell__status">${buttons}</span>`;
  }

  private applyView(list: AddressUpgrade[]): AddressUpgrade[] {
    const filtered = this.searchTerm
      ? list.filter(u =>
          u.name.toLowerCase().includes(this.searchTerm) ||
          (u.description?.toLowerCase().includes(this.searchTerm) ?? false))
      : list;
    const key = this.form.value.sortKey ?? 'roi_desc';
    const sorted = [...filtered];
    switch (key) {
      case 'cost_asc':
        sorted.sort((a, b) => (a.estimated_cost ?? Number.POSITIVE_INFINITY) - (b.estimated_cost ?? Number.POSITIVE_INFINITY));
        break;
      case 'impact_desc':
        sorted.sort((a, b) => (b.impact_score ?? 0) - (a.impact_score ?? 0));
        break;
      case 'recent':
        sorted.sort((a, b) => {
          const at = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bt - at;
        });
        break;
      case 'name_asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'roi_desc':
      default:
        sorted.sort((a, b) => (b.roi_score ?? 0) - (a.roi_score ?? 0));
        break;
    }
    return sorted;
  }

  private async goToPage(url: string, params: any, component: any) {
    if (this.windowService.isDesktopRes) {
      const isPanelOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isPanelOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription$?.unsubscribe();
      this.panelClosedSubscription$ = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(async () => {
        const didMakeChanges = await this.storage.retrieve('didMakeAddressUpgradeChanges');
        if (didMakeChanges) {
          await this.storage.save('didMakeAddressUpgradeChanges', false);
          await this.loadPage();
        }
      });
    } else {
      // Mobile: AddEdit page reads dialog-params from TidyStorage, so seed
      // it here. Mark dialog-right-side-open false so the page renders the
      // mobile top-nav layout instead of the right-side ion-content.
      await this.storage.save('dialog-right-side-open', false);
      await this.storage.save('dialog-params', params);
      this.navCtrl.navigateForward(url, params);
    }
  }
}
